/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { $createCodeNode } from "@lexical/code";
import { INSERT_CHECK_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from "@lexical/list";
import { INSERT_EMBED_COMMAND } from "@lexical/react/LexicalAutoEmbedPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { INSERT_HORIZONTAL_RULE_COMMAND } from "components/NewEditor/nodes/HorizontalRuleNode";
import { LexicalTypeaheadMenuPlugin, MenuOption, useBasicTypeaheadTriggerMatch } from "@lexical/react/LexicalTypeaheadMenuPlugin";
import { $createHeadingNode, $createQuoteNode } from "@lexical/rich-text";
import { $setBlocksType } from "@lexical/selection";
import { INSERT_TABLE_COMMAND } from "@lexical/table";
import { $createParagraphNode, $getSelection, $isRangeSelection, FORMAT_ELEMENT_COMMAND, LexicalEditor, TextNode } from "lexical";
import { useCallback, useMemo, useRef, useState } from "react";
import * as React from "react";
import * as ReactDOM from "react-dom";

import useModal from "../../hooks/useModal";

import { InsertEquationDialog } from "../EquationsPlugin";
import { InsertImageDialog } from "../ImagesPlugin";
import { InsertTableDialog } from "../TablePlugin";
import { InsertLinkPreviewDialog } from "../SlidLinkPreviewPlugin";
import { ReactComponent as ParagraphIcon } from "../../images/icons/dropdown-paragraph.svg";
import { ReactComponent as Heading1Icon } from "../../images/icons/dropdown-heading1.svg";
import { ReactComponent as Heading2Icon } from "../../images/icons/dropdown-heading2.svg";
import { ReactComponent as Heading3Icon } from "../../images/icons/dropdown-heading3.svg";
import { ReactComponent as BulletListIcon } from "../../images/icons/dropdown-bullet.svg";
import { ReactComponent as NumberedListIcon } from "../../images/icons/dropdown-number.svg";
import { ReactComponent as CheckListIcon } from "../../images/icons/dropdown-check.svg";
import { ReactComponent as TableIcon } from "../../images/icons/table.svg";
import { ReactComponent as DividerIcon } from "../../images/icons/divider.svg";
import { ReactComponent as ImageIcon } from "../../images/icons/image.svg";
import { ReactComponent as LinkPreviewIcon } from "../../images/icons/link-preview.svg";
import { ReactComponent as CodeBlockIcon } from "../../images/icons/code-block.svg";
import { ReactComponent as MathIcon } from "../../images/icons/math.svg";
import i18n from "config/i18n/i18n";
import { Typography13 } from "@slid/slid-ips";
import useEditorStore from "store/useEditorStore";
const { t } = i18n;

class ComponentPickerOption extends MenuOption {
  // What shows up in the editor
  title: string;
  // Icon for display
  icon?: JSX.Element;
  // For extra searching.
  keywords: Array<string>;
  // TBD
  keyboardShortcut?: string;
  // What happens when you select this option?
  onSelect: (queryString: string) => void;

  constructor(
    title: string,
    options: {
      icon?: JSX.Element;
      keywords?: Array<string>;
      keyboardShortcut?: string;
      onSelect: (queryString: string) => void;
    }
  ) {
    super(title);
    this.title = title;
    this.keywords = options.keywords || [];
    this.icon = options.icon;
    this.keyboardShortcut = options.keyboardShortcut;
    this.onSelect = options.onSelect.bind(this);
  }
}

function ComponentPickerMenuItem({
  index,
  isSelected,
  onClick,
  onMouseEnter,
  option,
}: {
  index: number;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: ComponentPickerOption;
}) {
  let className = "item";
  if (isSelected) {
    className += " selected";
  }
  return (
    <li
      key={option.key}
      tabIndex={-1}
      className={className}
      ref={option.setRefElement}
      role="option"
      aria-selected={isSelected}
      id={"typeahead-item-" + index}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      {option.icon}
      <Typography13 text={option.title} color={"--gray15"} type="span" />
    </li>
  );
}

function getDynamicOptions(editor: LexicalEditor, queryString: string) {
  const options: Array<ComponentPickerOption> = [];

  if (queryString == null) {
    return options;
  }

  const tableMatch = queryString.match(/^([1-9]\d?)(?:x([1-9]\d?)?)?$/);

  if (tableMatch !== null) {
    const rows = tableMatch[1];
    const colOptions = tableMatch[2] ? [tableMatch[2]] : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(String);

    options.push(
      ...colOptions.map(
        (columns) =>
          new ComponentPickerOption(`${rows}x${columns} Table`, {
            icon: <i className="icon table" />,
            keywords: ["table"],
            onSelect: () => editor.dispatchCommand(INSERT_TABLE_COMMAND, { columns, rows }),
          })
      )
    );
  }

  return options;
}

type ShowModal = ReturnType<typeof useModal>[1];

function getBaseOptions(editor: LexicalEditor, showModal: ShowModal) {
  return [
    new ComponentPickerOption(t("paragraph", { ns: "EditorToolbar" }), {
      icon: <ParagraphIcon />,
      keywords: ["normal", "paragraph", "p", "text"],
      onSelect: () =>
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, () => $createParagraphNode());
          }
        }),
    }),
    ...([1, 2, 3] as const).map(
      (n) =>
        new ComponentPickerOption(t(`heading${n}`, { ns: "EditorToolbar" }), {
          icon: n === 1 ? <Heading1Icon /> : n === 2 ? <Heading2Icon /> : <Heading3Icon />,
          keywords: ["heading", "header", `h${n}`],
          onSelect: () =>
            editor.update(() => {
              const selection = $getSelection();
              if ($isRangeSelection(selection)) {
                $setBlocksType(selection, () => $createHeadingNode(`h${n}`));
              }
            }),
        })
    ),
    new ComponentPickerOption(t("numberedList", { ns: "EditorToolbar" }), {
      icon: <NumberedListIcon />,
      keywords: ["numbered list", "ordered list", "ol"],
      onSelect: () => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined),
    }),
    new ComponentPickerOption(t("bulletList", { ns: "EditorToolbar" }), {
      icon: <BulletListIcon />,
      keywords: ["bulleted list", "unordered list", "ul"],
      onSelect: () => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined),
    }),
    new ComponentPickerOption(t("checkList", { ns: "EditorToolbar" }), {
      icon: <CheckListIcon />,
      keywords: ["check list", "todo list"],
      onSelect: () => editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined),
    }),
    new ComponentPickerOption(t("table", { ns: "EditorToolbar" }), {
      icon: <TableIcon />,
      keywords: ["table", "grid", "spreadsheet", "rows", "columns"],
      onSelect: () => showModal("Insert Table", (onClose) => <InsertTableDialog activeEditor={editor} onClose={onClose} />),
    }),
    new ComponentPickerOption(t("code-block", { ns: "EditorToolbar" }), {
      icon: <CodeBlockIcon />,
      keywords: ["javascript", "python", "js", "codeblock"],
      onSelect: () =>
        editor.update(() => {
          const selection = $getSelection();

          if ($isRangeSelection(selection)) {
            if (selection.isCollapsed()) {
              $setBlocksType(selection, () => $createCodeNode());
            } else {
              // Will this ever happen?
              const textContent = selection.getTextContent();
              const codeNode = $createCodeNode();
              selection.insertNodes([codeNode]);
              selection.insertRawText(textContent);
            }
          }
        }),
    }),
    new ComponentPickerOption(t("divider", { ns: "EditorToolbar" }), {
      icon: <DividerIcon />,
      keywords: ["horizontal rule", "divider", "hr"],
      onSelect: () => editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined),
    }),
    new ComponentPickerOption(t("math", { ns: "EditorToolbar" }), {
      icon: <MathIcon />,
      keywords: ["equation", "latex", "math"],
      onSelect: () => showModal("Insert Equation", (onClose) => <InsertEquationDialog activeEditor={editor} onClose={onClose} />),
    }),
    new ComponentPickerOption(t("image", { ns: "EditorToolbar" }), {
      icon: <ImageIcon />,
      keywords: ["image", "photo", "picture", "file"],
      onSelect: () => showModal("Insert Image", (onClose) => <InsertImageDialog activeEditor={editor} onClose={onClose} />),
    }),
    new ComponentPickerOption(t("link-preview", { ns: "EditorToolbar" }), {
      icon: <LinkPreviewIcon />,
      keywords: ["link preview", "link", "preview"],
      onSelect: () => showModal("Insert Link Preview", (onClose) => <InsertLinkPreviewDialog activeEditor={editor} onClose={onClose} />),
    }),
  ];
}

export default function ComponentPickerMenuPlugin(): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [modal, showModal] = useModal();
  const [queryString, setQueryString] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const isEditorFocused = useEditorStore((state) => state.isEditorFocused);

  const checkForTriggerMatch = useBasicTypeaheadTriggerMatch("/", {
    minLength: 0,
  });

  const options = useMemo(() => {
    const baseOptions = getBaseOptions(editor, showModal);

    if (!queryString) {
      return baseOptions;
    }

    const regex = new RegExp(queryString, "i");

    return [...getDynamicOptions(editor, queryString), ...baseOptions.filter((option) => regex.test(option.title) || option.keywords.some((keyword) => regex.test(keyword)))];
  }, [editor, queryString, showModal]);

  const onSelectOption = useCallback(
    (selectedOption: ComponentPickerOption, nodeToRemove: TextNode | null, closeMenu: () => void, matchingString: string) => {
      editor.update(() => {
        nodeToRemove?.remove();
        selectedOption.onSelect(matchingString);
        closeMenu();
      });
    },
    [editor]
  );

  return (
    <>
      {modal}
      <LexicalTypeaheadMenuPlugin<ComponentPickerOption>
        onQueryChange={setQueryString}
        onSelectOption={onSelectOption}
        triggerFn={checkForTriggerMatch}
        options={options}
        menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) =>
          anchorElementRef.current && options.length && isEditorFocused
            ? ReactDOM.createPortal(
                <div
                  className="typeahead-popover component-picker-menu"
                  ref={modalRef}
                  onMouseDown={(e) => {
                    // NOTE: when click on the menu, prevent the editor from losing focus
                    e.preventDefault();
                  }}
                >
                  <ul>
                    {options.map((option, i: number) => (
                      <ComponentPickerMenuItem
                        index={i}
                        isSelected={selectedIndex === i}
                        onClick={() => {
                          setHighlightedIndex(i);
                          selectOptionAndCleanUp(option);
                        }}
                        onMouseEnter={() => {
                          setHighlightedIndex(i);
                        }}
                        key={option.key}
                        option={option}
                      />
                    ))}
                  </ul>
                </div>,
                anchorElementRef.current
              )
            : null
        }
      />
    </>
  );
}
